'use client';

import React from 'react';
import { FileText, X, RefreshCw, MessageSquare } from 'lucide-react';

interface InspectCardModalProps {
  inspectCardId: string | null;
  inspectCardData: any | null;
  loadingCardDetail: boolean;
  onClose: () => void;
}

export const InspectCardModal: React.FC<InspectCardModalProps> = ({
  inspectCardId,
  inspectCardData,
  loadingCardDetail,
  onClose,
}) => {
  if (!inspectCardId) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0D1122] border border-slate-800 rounded-3xl max-w-2xl w-full p-6 space-y-6 shadow-2xl relative max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-400 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-white">{inspectCardData?.title || 'Card Detail'}</h3>
              <p className="text-xs text-slate-400 font-mono">
                List: <span className="text-indigo-400">{inspectCardData?.list?.title}</span> • Board: <span className="text-purple-400">{inspectCardData?.list?.board?.title}</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 space-y-5">
          {loadingCardDetail ? (
            <div className="py-16 text-center text-slate-400 space-y-3">
              <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin mx-auto" />
              <p className="text-xs font-medium">Fetching card attributes...</p>
            </div>
          ) : inspectCardData && (
            <>
              {/* Description */}
              <div className="space-y-2">
                <h4 className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Description</h4>
                <div className="bg-[#060811] border border-slate-800 rounded-2xl p-4 text-xs text-slate-200 leading-relaxed min-h-[60px]">
                  {inspectCardData.description || <span className="text-slate-600 italic">No description provided for this card.</span>}
                </div>
              </div>

              {/* Assignees & Due Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Assignees</h4>
                  <div className="bg-[#060811] border border-slate-800 rounded-2xl p-3 space-y-1.5">
                    {inspectCardData.assignees?.length > 0 ? (
                      inspectCardData.assignees.map((a: any) => (
                        <div key={a.user.id} className="flex items-center gap-2 text-xs text-slate-200">
                          <div className="w-6 h-6 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">
                            {a.user.name?.[0] || 'U'}
                          </div>
                          <span>{a.user.name}</span>
                          <span className="text-[10px] text-slate-500 font-mono">({a.user.email})</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-600 italic">No assignees</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Due Date</h4>
                  <div className="bg-[#060811] border border-slate-800 rounded-2xl p-3.5 text-xs text-slate-200 font-mono">
                    {inspectCardData.dueDate ? (
                      <span className="text-amber-400 font-bold">{new Date(inspectCardData.dueDate).toLocaleString()}</span>
                    ) : (
                      <span className="text-slate-600 italic">No due date set</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-3">
                <h4 className="text-xs font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Comments ({inspectCardData.comments?.length || 0})</span>
                </h4>

                {inspectCardData.comments?.length > 0 ? (
                  <div className="space-y-2">
                    {inspectCardData.comments.map((comm: any) => (
                      <div key={comm.id} className="bg-[#060811] border border-slate-800 rounded-2xl p-3.5 space-y-1">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-bold text-white">{comm.user?.name || 'User'}</span>
                          <span className="text-slate-500 font-mono text-[10px]">{new Date(comm.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">{comm.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-600 italic bg-[#060811] border border-slate-800 rounded-2xl p-4">No comments on this card.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
